import { signal, effect } from '@preact/signals';
import type { L1Code } from './types';

const KEY = 'english-learner-language';

type LabelKey = 'study' | 'flashcards' | 'quiz' | 'situations';

export type L1Meta = {
  flag: string;
  cjk: boolean;
  fontClass?: string;
  labels: Record<LabelKey, string>;
};

export const L1_META: Record<L1Code, L1Meta> = {
  ja: {
    flag: '🇯🇵',
    cjk: true,
    fontClass: 'font-japanese',
    labels: { study: '学習', flashcards: 'フラッシュカード', quiz: 'クイズ', situations: '状況' },
  },
  ko: {
    flag: '🇰🇷',
    cjk: true,
    fontClass: 'font-japanese',
    labels: { study: '학습', flashcards: '플래시카드', quiz: '퀴즈', situations: '상황' },
  },
  zh: {
    flag: '🇨🇳',
    cjk: true,
    fontClass: 'font-japanese',
    labels: { study: '学习', flashcards: '抽认卡', quiz: '测验', situations: '场景' },
  },
  es: {
    flag: '🇪🇸',
    cjk: false,
    labels: { study: 'Estudiar', flashcards: 'Tarjetas', quiz: 'Cuestionario', situations: 'Situaciones' },
  },
  fr: {
    flag: '🇫🇷',
    cjk: false,
    labels: { study: 'Étudier', flashcards: 'Cartes', quiz: 'Quiz', situations: 'Situations' },
  },
};

let stored: L1Code | null = null;
try {
  const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(KEY) : null;
  // Validate against L1_META — stale/corrupted values fall back to default 'ja'.
  stored = raw && raw in L1_META ? (raw as L1Code) : null;
} catch {
  // Safari private mode: getItem can throw.
}

export const learnerLanguage = signal<L1Code>(stored ?? 'ja');

effect(() => {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(KEY, learnerLanguage.value);
  } catch {
    // Safari private mode or quota exhausted: persistence silently degrades.
  }
});

// V1: only 'ja' is user-selectable. Others ship per-language in follow-up PRs after verified translations.
export const SELECTABLE_L1S = ['ja'] as const satisfies readonly L1Code[];

// Quiz L1 subtext toggle — level-agnostic signal. Enforcement of "only at B1"
// lives in src/quiz/quizL1Subtext.ts so renaming a level (or extending the
// toggle to B2) doesn't touch storage or persistence.
const QUIZ_L1_TOGGLE_KEY = 'english-show-l1-quiz-translations';

let storedQuizL1: boolean = true;
try {
  const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(QUIZ_L1_TOGGLE_KEY) : null;
  if (raw === '0') storedQuizL1 = false;
} catch {
  // Safari private mode: getItem can throw.
}

export const showL1QuizTranslations = signal<boolean>(storedQuizL1);

effect(() => {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(QUIZ_L1_TOGGLE_KEY, showL1QuizTranslations.value ? '1' : '0');
    }
  } catch {
    // Safari private mode or quota exhausted: persistence silently degrades.
  }
});
